import React, { useEffect } from 'react';
import { useHeadCollector } from './HeadContext';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: object;
  noIndex?: boolean;
}

/** Set or update a <meta> tag by name or property attribute */
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

/** Set or update a <link> tag by rel */
function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterCard = 'summary_large_image',
  jsonLd,
  noIndex = false,
}) => {
  const fullTitle = title.includes('Prismatic Forge') ? title : `${title} | Prismatic Forge`;
  const currentUrl = canonical || (typeof window !== 'undefined' ? window.location.href : '');

  // --- SSR path: push tags into HeadCollector context ---
  const collector = useHeadCollector();
  if (collector) {
    collector.push({ tag: 'title', children: fullTitle });
    collector.push({ tag: 'meta', attributes: { name: 'description', content: description } });
    if (keywords) collector.push({ tag: 'meta', attributes: { name: 'keywords', content: keywords } });
    if (noIndex) collector.push({ tag: 'meta', attributes: { name: 'robots', content: 'noindex,nofollow' } });
    if (canonical) collector.push({ tag: 'link', attributes: { rel: 'canonical', href: canonical } });

    // Open Graph
    collector.push({ tag: 'meta', attributes: { property: 'og:type', content: 'website' } });
    collector.push({ tag: 'meta', attributes: { property: 'og:title', content: ogTitle || fullTitle } });
    collector.push({ tag: 'meta', attributes: { property: 'og:description', content: ogDescription || description } });
    collector.push({ tag: 'meta', attributes: { property: 'og:url', content: ogUrl || currentUrl } });
    collector.push({ tag: 'meta', attributes: { property: 'og:site_name', content: 'Prismatic Forge' } });
    collector.push({ tag: 'meta', attributes: { property: 'og:locale', content: 'en_US' } });
    if (ogImage) {
      collector.push({ tag: 'meta', attributes: { property: 'og:image', content: ogImage } });
      collector.push({ tag: 'meta', attributes: { property: 'og:image:alt', content: ogTitle || fullTitle } });
    }

    // Twitter
    collector.push({ tag: 'meta', attributes: { name: 'twitter:card', content: twitterCard } });
    collector.push({ tag: 'meta', attributes: { name: 'twitter:title', content: ogTitle || fullTitle } });
    collector.push({ tag: 'meta', attributes: { name: 'twitter:description', content: ogDescription || description } });
    if (ogImage) collector.push({ tag: 'meta', attributes: { name: 'twitter:image', content: ogImage } });

    // Additional
    collector.push({ tag: 'meta', attributes: { name: 'author', content: 'Prismatic Forge' } });
    collector.push({ tag: 'meta', attributes: { name: 'application-name', content: 'Prismatic Forge' } });
    collector.push({ tag: 'meta', attributes: { name: 'apple-mobile-web-app-title', content: 'Prismatic Forge' } });

    // JSON-LD
    if (jsonLd) {
      collector.push({ tag: 'script', attributes: { type: 'application/ld+json' }, children: JSON.stringify(jsonLd) });
    }
  }

  // --- Client path: manipulate document.head directly ---
  useEffect(() => {
    document.title = fullTitle;

    // Basic meta
    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    if (canonical) setLink('canonical', canonical);
    if (noIndex) setMeta('name', 'robots', 'noindex,nofollow');

    // Open Graph
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:title', ogTitle || fullTitle);
    setMeta('property', 'og:description', ogDescription || description);
    setMeta('property', 'og:url', ogUrl || currentUrl);
    setMeta('property', 'og:site_name', 'Prismatic Forge');
    setMeta('property', 'og:locale', 'en_US');
    if (ogImage) {
      setMeta('property', 'og:image', ogImage);
      setMeta('property', 'og:image:alt', ogTitle || fullTitle);
    }

    // Twitter
    setMeta('name', 'twitter:card', twitterCard);
    setMeta('name', 'twitter:title', ogTitle || fullTitle);
    setMeta('name', 'twitter:description', ogDescription || description);
    if (ogImage) setMeta('name', 'twitter:image', ogImage);

    // Additional
    setMeta('name', 'author', 'Prismatic Forge');
    setMeta('name', 'application-name', 'Prismatic Forge');
    setMeta('name', 'apple-mobile-web-app-title', 'Prismatic Forge');

    // JSON-LD
    if (jsonLd) {
      const id = 'seo-jsonld';
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }
  }, [fullTitle, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogUrl, twitterCard, jsonLd, noIndex, currentUrl]);

  return null;
};

export default SEOHead;
